import { Controller } from "@hotwired/stimulus";
import { toastManager } from "../components/DownloadTray/ToastManager";

// GR-1965 — mirrors EventRequestCustomFieldDefinition::OTHER_VALUE, the
// sentinel a choice field carries while its free-text answer is being typed.
const OTHER_VALUE = "__other__";

/**
 * Event Request Wizard Controller
 *
 * Handles wizard form validation and dynamic field updates.
 * Saves are explicit (via Save & Continue button), no auto-save.
 */
export default class extends Controller {
  static targets = ["form", "dietaryNotesSection"];
  static values = {
    currentStep: Number,
  };

  connect() {
    // No auto-save - saves are explicit via form submission.
    // Surface unexpected non-success responses (e.g. WAF-level 403s that
    // can't be re-routed to a flash message) so the form doesn't silently
    // reset for the user.
    this.handleSubmitEnd = this.handleSubmitEnd.bind(this);
    this.element.addEventListener("turbo:submit-end", this.handleSubmitEnd);
  }

  disconnect() {
    this.element.removeEventListener("turbo:submit-end", this.handleSubmitEnd);
  }

  handleSubmitEnd(event) {
    if (event.detail?.success) return;

    const status = event.detail?.fetchResponse?.response?.status;
    if (status === 422) return;

    const message = status === 403
      ? "We couldn't save your changes. Some of the text you entered may not be accepted — please try simpler characters."
      : "We couldn't save your changes. Please try again or contact support if this keeps happening.";

    toastManager.show({
      message,
      variant: "danger",
      icon: "alert",
      autoCloseMs: 20000,
    });
  }

  /**
   * GR-3289 — a select custom field is a Primer SelectPanel rather than a
   * native <select>, because a 277-row native dropdown's only search is
   * single-letter prefix jumping.
   *
   * The panel emits `itemActivated` instead of a `change`, and it does not
   * carry the form value itself, so the hidden field beside it is what
   * actually submits and this is what keeps the two in step. The wire format
   * is unchanged: same name, same __other__ sentinel, same server-side
   * collapsing.
   */
  customFieldSelected(event) {
    const panel = event.currentTarget;
    const field = this.element.querySelector(
      `[data-custom-field-value-for="${panel.id}"]`,
    );
    if (field) field.value = event.detail?.value ?? "";

    this.revealOther(event);
  }

  /**
   * GR-1965 — reveal the free-text "Other" input for a choice custom field.
   * Triggered by the select panel's itemActivated (GR-3289) or the checklist
   * "Other" checkbox. The trigger carries data-other-key matching the input's
   * data-other-input-for.
   */
  revealOther(event) {
    const trigger = event.currentTarget || event.target;
    const key = trigger.dataset.otherKey;
    if (!key) return;

    const input = this.element.querySelector(`[data-other-input-for="${key}"]`);
    if (!input) return;

    if (this.otherIsChosen(trigger, event)) {
      input.classList.remove("d-none");
      this.focusOther(input, trigger);
    } else {
      input.classList.add("d-none");
      input.value = "";
    }
  }

  /**
   * The same question in three shapes. The checklist's "Other" is a checkbox;
   * a native <select> carries the sentinel as its own value; a SelectPanel
   * keeps its selection internally and reports the activated item's value on
   * the event instead.
   */
  otherIsChosen(trigger, event) {
    if (trigger.tagName === "SELECT-PANEL") {
      return event.detail?.value === OTHER_VALUE;
    }
    if (trigger.type === "checkbox") return trigger.checked;
    return trigger.value === OTHER_VALUE;
  }

  /**
   * Primer defers the panel's hide() to the next turn so that focus changes
   * cannot retarget the activation event, and closing the dialog hands focus
   * back to the show button. Focusing on the next turn too puts the cursor in
   * the box the requester just asked for rather than back on the button.
   */
  focusOther(input, trigger) {
    if (trigger.tagName === "SELECT-PANEL") {
      setTimeout(() => input.focus());
    } else {
      input.focus();
    }
  }

  /**
   * Toggle consumable headcount field enabled/disabled state
   * Also shows/hides dietary notes section
   */
  toggleConsumableHeadcount(event) {
    const checkbox = event.target;
    const row = checkbox.closest('[class*="Box-row"]') || checkbox.closest('.d-flex');
    const headcountInput = row?.querySelector('input[type="number"]');

    if (headcountInput) {
      headcountInput.disabled = !checkbox.checked;
    }

    // Show/hide dietary notes section based on any consumable being selected
    this.updateDietaryNotesVisibility();
  }

  /**
   * Update dietary notes section visibility
   */
  updateDietaryNotesVisibility() {
    if (!this.hasDietaryNotesSectionTarget) return;

    const anyConsumableChecked = this.element.querySelectorAll(
      'input[name="consumable_ids[]"]:checked'
    ).length > 0;

    // GR-3527: Included Catering (Breakfast/Lunch) has no checkbox — it is
    // booked by typing a cover count, and a covers-only order still needs the
    // setup and delivery box. On an org whose catering catalog is entirely
    // included items, checkboxes alone never open it.
    const anyCoversRequested = Array.from(
      this.element.querySelectorAll('input[name^="additional_bl_covers["]')
    ).some((input) => parseInt(input.value, 10) > 0);

    // GR-3527 review: per-day catering posts its own checkboxes, so a per-day
    // order is just as much a booking as a shared one. Checkboxes only: the
    // per-day headcount fields share the day_consumables prefix.
    //
    // :not(:disabled) matters. In shared mode the per-day rows are mirrors of
    // the shared selection and are disabled, so they never post, but :checked
    // still matches a disabled input. Without this, unticking the last shared
    // item left the box open (this controller is on the <form> and binds
    // before event-request-facility unchecks the mirrors) and a note typed
    // into it was dropped on save. Counting only what actually posts also
    // removes the dependence on listener order.
    const anyDayConsumableChecked = this.element.querySelectorAll(
      'input[type="checkbox"][name^="day_consumables["]:checked:not(:disabled)'
    ).length > 0;

    if (anyConsumableChecked || anyCoversRequested || anyDayConsumableChecked) {
      this.dietaryNotesSectionTarget.classList.remove("d-none");
    } else {
      this.dietaryNotesSectionTarget.classList.add("d-none");
    }
  }
}
